import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ReactComponent as OpenNewWindowIcon } from '@assets/images/Open_New_Window_Icon.svg';
import analytic from '@global/utils/AdobeAnalytics';
import { NotificationTypes } from '@src/global/components/Notification';
import { ButtonCTA } from '@src/global/components/UI Elements/ButtonCTA';
import { PageLayerType } from '@src/global/utils/AdobeAnalytics/types';

import './style.scss';
import { vitalSourceHTMLError, vitalSourceHTMLRedirect } from '../../consts';
import { getVSLink } from '@src/global/requests/subscriptionDetails/GET_VITAL_SOURCE_LINK';
import {
  customParseJwt,
  getTokensFromCookies,
} from '@src/global/hooks/useAuth/utils';

interface VitalSourceButtonProps {
  subscriptionUid: string;
  setShowNotification: (arg: [NotificationTypes, string]) => void;
}

export const VitalSourceButton = ({
  setShowNotification,
}: VitalSourceButtonProps) => {
  const { t } = useTranslation();
  const { almToken } = getTokensFromCookies();
  const { alm_guid } = customParseJwt(almToken);

  const [loadingVS, setLoadingVS] = useState(false);

  const getVSAccessLink = useCallback(async () => {
    setLoadingVS(true);
    try {
      const response = await getVSLink(alm_guid);
      setLoadingVS(false);
      return { data: response.data };
    } catch (error) {
      setLoadingVS(false);
      return { error };
    }
  }, [alm_guid]);

  const pageLayerProps: PageLayerType = {
    page: {
      name: 'vital source bookshelf',
      section: 'subscription details',
      subSection: 'vital source bookshelf',
    },
    event: 'access vitalsource bookshelf',
  };

  const handleVitalSource = useCallback(async () => {
    // New tab needs to exist as a bridge between React app and VitalSource
    // Tab needs to be opened immediately to avoid pop-up blockers
    analytic.dataLayerBtnTracker({
      btnType: 'cta',
      btnName: t('subDetails.sub.vitalSourceCta'),
    });
    analytic.dataLayerPageLoaded(pageLayerProps);
    const vitalSourceRedirect = window.open('', '_blank');
    vitalSourceRedirect.document.write(vitalSourceHTMLRedirect);

    const { data, error } = await getVSAccessLink();

    const accessLink = data;
    if (accessLink) {
      vitalSourceRedirect.location.href = accessLink;
    } else {
      vitalSourceRedirect.document.write(vitalSourceHTMLError);
    }

    if (error) {
      console.error(error);
      setShowNotification([
        NotificationTypes.error,
        t('notificationTexts.err.vitalSource'),
      ]);
    }
  }, []);

  const Button = useCallback(
    () => (
      <ButtonCTA
        variant="primary"
        onClick={handleVitalSource}
        disabled={loadingVS}
      >
        {loadingVS ? (
          <div className="spinner"></div>
        ) : (
          <span className="vitalSource-text-svg">
            {t('subDetails.sub.vitalSourceCta')} <OpenNewWindowIcon />
          </span>
        )}
      </ButtonCTA>
    ),
    [loadingVS, handleVitalSource]
  );

  return <Button />;
};
